<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import '@patsnap-ui/icon/assets/solid/export.svg';
import '@patsnap-ui/icon/assets/solid/stop.svg';
import '@patsnap-ui/icon/assets/solid/ArrowLeft.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import IconRegenerate from '@/presentation/components/icons/IconRegenerate.vue';
import SvgIcon from '@/presentation/components/common/svg-icon/SvgIcon.vue';
// import Feedback from '@analytics/shared/components/feedback/Feedback.vue'; todo
import {
    AIDisclosureParagraphEditType,
    type AIDisclosureParagraphType,
} from '@/services/ai-disclosure/type/ai-disclosure.enum';
import { disclosureOptimizationTypeMap } from '@/presentation/pages/ai-disclosure/components/constants';
import { useAiDisclosureResult } from '@/composables/use-ai-disclosure-result';
import { StreamStatus } from '@/services/ai-disclosure/type/ai-disclosure.enum';
import { useHistory } from '@/composables/use-history';

const CONTENT_CACHE_MAX_NUMBER = 5;
const PREV_CONTENT = 'prev';
const NEXT_CONTENT = 'next';
const AdditionDisclosureOptimizationTypes = [
    AIDisclosureParagraphEditType.ADD_BACKGROUND,
    AIDisclosureParagraphEditType.ADD_REFERENCE,
    AIDisclosureParagraphEditType.ADD_DRAWBACKS,
    AIDisclosureParagraphEditType.ADD_PROBLEM,
    AIDisclosureParagraphEditType.ADD_EFFECT,
    AIDisclosureParagraphEditType.ADD_EMBODIMENT
];

const { t } = useI18n();
const { historyData } = useHistory();

const paragraphEditRef = ref(null);
const cacheContents = ref<string[]>(['']);
const currentContentIndex = ref(1);
const isGenerating = ref(false);

const { aiDisclosureResultService } = useAiDisclosureResult();

const props = defineProps<{
    selectedText: string;
    isSelection: boolean;
    floatingStyles: { [key: string]: string };
    editType: AIDisclosureParagraphEditType | undefined;
    editingParagraph: AIDisclosureParagraphType | undefined;
}>();

const emits = defineEmits<{
    (event: 'discard'): void;
    (event: 'replace', newText: string): void;
    (event: 'addition', newText: string): void;
}>();

const editTypeInfo = computed(() => {
    return disclosureOptimizationTypeMap.find((item) => item.disclosureParagraphEditType === props.editType);
});

const isAddition = computed(() => {
    return AdditionDisclosureOptimizationTypes.some(item => item === props.editType);
});

// const feedbackParams = computed(() => {
//     const taskId = historyData.value.historyDetailTemp.taskId;
//     return {
//         product: 'ANALYTICS-AI-LAB',
//         module: 'https://analytics.zhihuiya.com/ai-lab/#/disclosure',
//         page_params: `?taskId=${taskId}`,
//         query: `{"task_Id":${taskId}}`,
//         question: `${props.editType}:${props.selectedText}`, // 类型：文字
//         answer: cacheContents.value[currentContentIndex.value - 1], // 回答的内容
//     };
// });

function handleDiscard() {
    emits('discard');
}

function handleSubmit() {
    if (isAddition.value) {
        emits('addition', cacheContents.value[currentContentIndex.value - 1]);
    } else {
        emits('replace', cacheContents.value[currentContentIndex.value - 1]);
    }
}

function handleChangeCurrentContentIndex(type: string | undefined) {
    if (type === PREV_CONTENT) {
        currentContentIndex.value = Math.max(1, currentContentIndex.value - 1);
    } else {
        currentContentIndex.value = Math.min(cacheContents.value.length, currentContentIndex.value + 1);
    }
}

function stopGenerate() {
    isGenerating.value = false;
    aiDisclosureResultService.stopGenerate();
}

function generateContent() {
    if (cacheContents.value.length < CONTENT_CACHE_MAX_NUMBER && props.editType) {
        isGenerating.value = true;

        aiDisclosureResultService.generateContent({
            contentType: props.editType as AIDisclosureParagraphEditType,
            text: props.selectedText,
        }, (status: StreamStatus, content?: string) => {
            cacheContents.value = [...cacheContents.value];

            if (status === StreamStatus.PROCESSING) {
                if (content) {
                    cacheContents.value[currentContentIndex.value - 1] = content;
                }
            } else {
                isGenerating.value = false;
            }
        });
    }
}

function handleRegenerate() {
    if (cacheContents.value.length < CONTENT_CACHE_MAX_NUMBER) {
        currentContentIndex.value += 1;
        cacheContents.value.push('');

        generateContent();
    }
}

function scrollPosition() {
    if (!paragraphEditRef.value) return;
    if (props.isSelection) {
        // 选中情况下，判断弹框是否在可视区域内，在,则不再滚动
        const { top: eleTop = 0, height: eleHeight = 0 } = paragraphEditRef.value.getBoundingClientRect();
        const windowHeight = document.documentElement.clientHeight;
        if (eleTop + eleHeight < windowHeight) return;
    }

    nextTick(() => {
        paragraphEditRef.value.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    });
}

watch(() => props.floatingStyles, () => {
    scrollPosition();
}, {
    deep: true,
});

onMounted(() => {
    generateContent();
    scrollPosition();
});
</script>

<template>
    <div ref="paragraphEditRef" class="floating-disclosure-content-edit lab-border-rounded w-[640px] p-4">
        <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
                <div class="flex items-center gap-1">
                    <SvgIcon
                        class="h-6 w-6 cursor-pointer fill-grey-450"
                        icon="SolidArrowLeft"
                        @click.native="handleChangeCurrentContentIndex(PREV_CONTENT)"
                    />
                    <span class="text-sm text-grey-90">{{ currentContentIndex }}/{{ cacheContents.length }}</span>
                    <SvgIcon
                        class="mr-1 h-6 w-6 cursor-pointer fill-grey-450"
                        icon="SolidArrowRight"
                        @click.native="handleChangeCurrentContentIndex(NEXT_CONTENT)"
                    />
                </div>

                <div class="flex items-center gap-1 p-1">
                    <component :is="editTypeInfo.icon" />

                    <span class="text-sm text-black">
                        {{
                            t(editTypeInfo.labelKey)
                        }}
                    </span>

                    <span v-if="isSelection">
                        ({{ t('aiDisclosure.forSelectedText') }})
                    </span>
                </div>
            </div>

            <button v-if="isGenerating" class="th-btn th-btn-secondary pl-2" @click="stopGenerate">
                <SvgIcon
                    class="th-btn-icon h-6 w-6 !fill-grey-450"
                    icon="SolidStop"
                />

                <span>
                    {{ t('aiDisclosureGlobal.globalBtn.stop') }}
                </span>
            </button>

            <button v-else class="th-btn th-btn-outline pl-2" @click="handleRegenerate">
                <IconRegenerate
                    class="th-btn-icon h-6 w-6 !fill-grey-450"
                />

                <span>
                    {{ t('aiDisclosureGlobal.globalBtn.regenerate') }}
                </span>
            </button>
        </div>

        <textarea
            ref="commentTextarea"
            v-model="cacheContents[currentContentIndex - 1]"
            class="my-4 h-[164px] w-full resize-none rounded-lg border border-grey-40 p-3 text-black"
        />

        <div class="flex items-center justify-between">
            <!-- <Feedback
                class="justify-center"
                type="Disclosure"
                :params="feedbackParams"
            /> -->

            <span class="flex items-center gap-2">
                <button class="th-btn th-btn-secondary" @click="handleDiscard">
                    {{
                        t('aiDisclosureGlobal.globalBtn.cancel')
                    }}
                </button>

                <button
                    class="th-btn-primary th-btn"
                    :disabled="isGenerating"
                    @click="handleSubmit"
                >
                    {{
                        isAddition ? t('aiDisclosureGlobal.globalBtn.submit') : t('aiDisclosureGlobal.globalBtn.replace')
                    }}
                </button>
            </span>
        </div>
    </div>
</template>

<style lang="scss">
.floating-disclosure-content-edit {
    textarea {
        &:focus-visible {
            outline: none !important;
            border-color: #0764E9;
        }
    }
}
</style>
